import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    TextField,
    Button,
    Chip
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GetAppIcon from '@mui/icons-material/GetApp';
import '../../../node_modules/tabulator-tables/dist/css/tabulator_bootstrap4.css';
import { TabulatorFull as Tabulator, ColumnDefinition, ColumnComponent } from 'tabulator-tables';
import { useAlert } from '../modules/services/Bundle';
import { useAuth } from '../modules/auth';
import { useFiltrosAtivos } from '../../components/FiltrosGlobais';

export interface HistoricoAtivo {
    id: number;
    ativo: string;
    veiculo: string;
    associadoEm: string;
    desassociadoEm: string | null;
    duracaoMinutos: number;
    tempo: string;
}

interface HistoricTabProps {
    selectedCorporationName: string;
    selectedOrganizationName: string;
    canDisplay: boolean;
    isActive: boolean; // Indica se a aba está ativa
    onRowClick?: (historico: HistoricoAtivo) => void; // Callback para clique na linha
    filtrosAplicados?: Record<string, any>; // Filtros aplicados do sidebar (campo -> valor)
    onOpenFiltros?: () => void; // Callback para abrir o sidebar de filtros
    filtrosCount?: number; // Número de filtros aplicados
}

const HistoricTab: React.FC<HistoricTabProps> = ({
    selectedCorporationName,
    selectedOrganizationName,
    canDisplay,
    isActive,
    onRowClick,
    filtrosAplicados = {},
    onOpenFiltros,
    filtrosCount = 0
}) => {
    const alert = useAlert();
    const { auth } = useAuth();
   const { selectedOrganizations, isInitialized, selectedCorporation, availableCorporations } = useFiltrosAtivos();

  // Verificação se é VWCO
  const isVwco = availableCorporations.find(c => c.id === selectedCorporation)?.name === "VWCO";
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const [historico, setHistorico] = useState<HistoricoAtivo[]>([]);
    const [loadingHistorico, setLoadingHistorico] = useState(false);
    const [linhas, setLinhas] = useState<number>(10);
    const [offset, setOffset] = useState<number>(0);
    const [totalHistorico, setTotalHistorico] = useState<number>(0);
    const [diasHistorico, setDiasHistorico] = useState<number>(10);
    const tableRef = useRef<HTMLDivElement | null>(null);
    const tabulatorRef = useRef<Tabulator | null>(null);

    const formatarDuracao = useCallback((minutos: number): string => {
        return `${minutos} min`;
    }, []);

    const formatarTempo = useCallback((tempo: string): string => {
        if (!tempo) return '-';
        try {
            const partes = tempo.split('.');
            if (partes.length === 2) {
                const [dias, tempoRestante] = partes;
                const diasNum = parseInt(dias);
                if (diasNum > 0) {
                    return `${diasNum} dia${diasNum > 1 ? 's' : ''}, ${tempoRestante}`;
                }
                return tempoRestante;
            }
        } catch (e) {
            console.warn('Erro ao formatar tempo:', tempo);
        }
        return tempo;
    }, []);

    const tabulatorHandlersRef = useRef<{
        formatarTempo: (tempo: string) => string;
        formatarDuracao: (minutos: number) => string;
        onRowClick?: (historico: HistoricoAtivo) => void;
    }>({
        formatarTempo,
        formatarDuracao,
        onRowClick
    });

    useEffect(() => {
        tabulatorHandlersRef.current = {
            formatarTempo,
            formatarDuracao,
            onRowClick
        };
    }, [formatarTempo, formatarDuracao, onRowClick]);

    // Buscar histórico quando os parâmetros mudarem (apenas se a aba estiver ativa)
    useEffect(() => {
        // Só buscar se a aba estiver ativa
        if (!isActive) {
            console.log('⏸️ [HISTÓRICO] Aba não está ativa, não fazendo nada');
            return;
        }

        if (!isInitialized || !canDisplay || !selectedCorporationName || !selectedOrganizationName) {
            console.log('⚠️ [HISTÓRICO] Condições não atendidas, não fazendo nada');
            // Não limpar dados se apenas a aba mudou, apenas se as condições não forem atendidas
            return;
        }

        let cancelled = false;

        const fetchHistorico = async () => {
            if (cancelled) return;
            setLoadingHistorico(true);
            try {
                const encodedCorporacao = encodeURIComponent(selectedCorporationName.trim());
                const encodedOrganizacao = encodeURIComponent(selectedOrganizationName.trim());
                const endpoint = `/api/associacoes/${encodedCorporacao}/${encodedOrganizacao}/Associacoes/historico`;

                // Formatar data/hora no formato YYYY-MM-DDTHH:mm:ss (sem milissegundos e sem Z)
                const formatarDataHora = (data: Date): string => {
                    // Garantir que estamos usando a data local, não UTC
                    const ano = data.getFullYear();
                    const mes = String(data.getMonth() + 1).padStart(2, '0');
                    const dia = String(data.getDate()).padStart(2, '0');
                    const hora = String(data.getHours()).padStart(2, '0');
                    const minuto = String(data.getMinutes()).padStart(2, '0');
                    const segundo = String(data.getSeconds()).padStart(2, '0');
                    const resultado = `${ano}-${mes}-${dia}T${hora}:${minuto}:${segundo}`;
                    console.log('🕐 [HISTÓRICO] Data formatada:', resultado, 'de:', data);
                    return resultado;
                };

                // DtFinal sempre será a data e hora atual da requisição
                const dataFinal = new Date();
                const dtFinal = formatarDataHora(dataFinal);
                console.log('🕐 [HISTÓRICO] DtFinal:', dtFinal);

                // DtInicial será DtFinal menos DiasHistorico dias
                const dtInicialDate = new Date();
                dtInicialDate.setDate(dtInicialDate.getDate() - diasHistorico);
                // Definir hora para 00:00:00 no início do dia
                dtInicialDate.setHours(0, 0, 0, 0);
                const dtInicial = formatarDataHora(dtInicialDate);
                console.log('🕐 [HISTÓRICO] DtInicial:', dtInicial, '(DiasHistorico:', diasHistorico, ')');

                const requestBody: any = {
                    DtInicial: dtInicial,
                    DtFinal: dtFinal,
                    Linhas: linhas,
                    OffSet: offset
                };

                // Adicionar campos de filtro se houver filtros aplicados
                if (filtrosAplicados && Object.keys(filtrosAplicados).length > 0) {
                    // Pegar o primeiro filtro aplicado (campo e valor)
                    const primeiroFiltro = Object.entries(filtrosAplicados)[0];
                    if (primeiroFiltro && primeiroFiltro[0] && primeiroFiltro[1]) {
                        requestBody.Campo = primeiroFiltro[0];
                        requestBody.CampoValor = primeiroFiltro[1];
                        console.log('🔍 [HISTÓRICO] Adicionando filtros ao body:', {
                            Campo: requestBody.Campo,
                            CampoValor: requestBody.CampoValor
                        });
                    }
                }

                // Validar que as datas têm hora incluída
                if (!dtInicial.includes('T') || !dtFinal.includes('T')) {
                    console.error('❌ [HISTÓRICO] ERRO: Datas não contêm hora!', { dtInicial, dtFinal });
                }

                const fullUrl = `${backendUrl}${endpoint}`;
                console.log('🔵 [HISTÓRICO] Fazendo requisição POST para:', fullUrl);
                console.log('📦 [HISTÓRICO] Body da requisição:', JSON.stringify(requestBody, null, 2));
                console.log('📦 [HISTÓRICO] DtInicial (verificação):', requestBody.DtInicial);
                console.log('📦 [HISTÓRICO] DtFinal (verificação):', requestBody.DtFinal);

                const response = await fetch(fullUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${auth?.Token}`
                    },
                    body: JSON.stringify(requestBody)
                });

                console.log('📊 [HISTÓRICO] Status da resposta:', response.status, response.statusText);

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('❌ [HISTÓRICO] Erro na requisição:', response.status, errorText);
                    setHistorico([]);
                    setTotalHistorico(0);
                    return;
                }

                const historicoData: any[] = await response.json();
                console.log('✅ [HISTÓRICO] Dados recebidos da API:', historicoData.length, 'itens');

                if (Array.isArray(historicoData) && historicoData.length > 0) {
                    const historicoMapeado: HistoricoAtivo[] = historicoData.map((item: any, index: number) => {
                        const calcularMinutos = (tempo: string): number => {
                            if (!tempo) return 0;
                            try {
                                const partes = tempo.split('.');
                                if (partes.length === 2) {
                                    const [dias, tempoRestante] = partes;
                                    const [horas, minutos, segundos] = tempoRestante.split(':').map(Number);
                                    const totalMinutos = (parseInt(dias) * 24 * 60) + (horas * 60) + minutos + (segundos / 60);
                                    return Math.round(totalMinutos);
                                }
                            } catch (e) {
                                console.warn('Erro ao calcular minutos do tempo:', tempo);
                            }
                            return 0;
                        };

                        return {
                            id: index + 1,
                            ativo: item.Ativo || '',
                            veiculo: item.ItemAssociado || '',
                            associadoEm: item.DataAssociacao || '',
                            desassociadoEm: item.DataDesassociacao || null,
                            duracaoMinutos: calcularMinutos(item.Tempo || ''),
                            tempo: item.Tempo || ''
                        };
                    });

                    setHistorico(historicoMapeado);

                    if (historicoMapeado.length < linhas) {
                        setTotalHistorico(offset + historicoMapeado.length);
                    } else {
                        setTotalHistorico(offset + linhas + 1);
                    }
                } else {
                    setHistorico([]);
                    setTotalHistorico(0);
                }
            } catch (error) {
                console.error('❌ [HISTÓRICO] Erro ao buscar histórico:', error);
                alert.createMessage({
                    icon: 'error',
                    title: 'Erro',
                    message: 'Erro ao carregar histórico de associações',
                });
                setHistorico([]);
                setTotalHistorico(0);
            } finally {
                setLoadingHistorico(false);
            }
        };

        fetchHistorico();

        return () => {
            cancelled = true;
        };
    }, [isActive, selectedCorporationName, selectedOrganizationName, diasHistorico, linhas, offset, canDisplay, isInitialized, backendUrl, auth?.Token, filtrosAplicados]);

    const handleLinhasChange = (event: any) => {
        const novasLinhas = parseInt(event.target.value, 10);
        setLinhas(novasLinhas);
        setOffset(0);
    };

    const handleDiasHistoricoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const novosDias = parseInt(event.target.value, 10);
        if (!isNaN(novosDias) && novosDias > 0) {
            setDiasHistorico(novosDias);
            setOffset(0);
        }
    };

    const handlePaginaAnterior = () => {
        if (offset > 0) {
            setOffset(Math.max(0, offset - linhas));
        }
    };

    const handleProximaPagina = () => {
        if (totalHistorico > 0) {
            if (offset + linhas < totalHistorico) {
                setOffset(offset + linhas);
            }
        } else {
            setOffset(offset + linhas);
        }
    };

    const getPaginaAtual = () => {
        return Math.floor(offset / linhas) + 1;
    };

    const getTotalPaginas = () => {
        return Math.ceil(totalHistorico / linhas);
    };

    const dateRangeHeaderFilterEditor = useCallback((cell: any, onRendered: any, success: any, cancel: any) => {
        const container = document.createElement('span');
        container.style.display = 'flex';
        container.style.flexDirection = 'column';
        container.style.gap = '4px';

        const startInput = document.createElement('input');
        startInput.type = 'date';
        startInput.style.padding = '4px';

        const endInput = document.createElement('input');
        endInput.type = 'date';
        endInput.style.padding = '4px';

        const update = () => success({ date1: startInput.value, date2: endInput.value });
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                update();
            }
            if (event.key === 'Escape') {
                cancel();
            }
        };

        [startInput, endInput].forEach((input) => {
            input.addEventListener('change', update);
            input.addEventListener('blur', update);
            input.addEventListener('keydown', handleKey);
        });

        container.appendChild(startInput);
        container.appendChild(endInput);
        return container;
    }, []);

    const normalizeDateValue = (value?: string | null) => {
        if (!value) return null;

        const dateTimeMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/);
        if (dateTimeMatch) {
            const [, day, month, year, hours, minutes, seconds] = dateTimeMatch;
            return new Date(
                parseInt(year, 10),
                parseInt(month, 10) - 1,
                parseInt(day, 10),
                parseInt(hours, 10),
                parseInt(minutes, 10),
                parseInt(seconds, 10)
            );
        }

        const dateMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (dateMatch) {
            const [, day, month, year] = dateMatch;
            return new Date(
                parseInt(year, 10),
                parseInt(month, 10) - 1,
                parseInt(day, 10)
            );
        }

        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
    };

    const dateRangeHeaderFilterFunc = useCallback((headerValue: { date1?: string; date2?: string }, rowValue?: string) => {
        if (!headerValue || (!headerValue.date1 && !headerValue.date2)) return true;
        if (!rowValue) return false;

        const rowDate = normalizeDateValue(rowValue);
        if (!rowDate) return false;

        // Função auxiliar para criar data local a partir da string YYYY-MM-DD
        const createLocalDate = (dateStr: string, isEnd: boolean) => {
            if (!dateStr) return null;
            const [year, month, day] = dateStr.split('-').map(Number);
            // Mês em JS é 0-indexed (0-11)
            const date = new Date(year, month - 1, day);

            if (isEnd) {
                date.setHours(23, 59, 59, 999);
            } else {
                date.setHours(0, 0, 0, 0);
            }
            return date;
        };

        const startDate = headerValue.date1 ? createLocalDate(headerValue.date1, false) : null;
        const endDate = headerValue.date2 ? createLocalDate(headerValue.date2, true) : null;

        if (startDate && endDate) {
            return rowDate >= startDate && rowDate <= endDate;
        }

        if (startDate) return rowDate >= startDate;
        if (endDate) return rowDate <= endDate;

        return true;
    }, []);
    // Definição das colunas base para o Histórico
    const getHistoricoBaseColumns = (): ColumnDefinition[] => [
        {
            title: isVwco ? "Prisma" : "Ativo",
            field: 'ativo',
            sorter: 'string',
            headerFilter: 'input',
            headerFilterPlaceholder: 'Filtrar ativo',
            width: 378
        },
        {
            title: isVwco ? "Placa" : 'Veículo',
            field: 'veiculo',
            sorter: 'string',
            headerFilter: 'input',
            headerFilterPlaceholder: 'Filtrar veículo',
            width: 378
        },
        {
            title: isVwco ? "Data da associação" : 'Associado em',
            field: 'associadoEm',
            sorter: 'string',
            headerFilter: dateRangeHeaderFilterEditor,
            headerFilterFunc: dateRangeHeaderFilterFunc,
            headerFilterPlaceholder: 'Filtrar data',
            width: 378
        },
        {
            title: isVwco ? "Data da desassociação" : 'Desassociado em',
            field: 'desassociadoEm',
            sorter: 'string',
            headerFilter: dateRangeHeaderFilterEditor,
            headerFilterFunc: dateRangeHeaderFilterFunc,
            headerFilterPlaceholder: 'Filtrar data',
            width: 378,
            formatter: (cell) => cell.getValue() || '-'
        },
        {
            title: isVwco ? "Tempo decorrido" : 'Tempo',
            field: 'tempo',
            sorter: 'string',
            headerFilter: 'input',
            headerFilterPlaceholder: 'Filtrar tempo',
            formatter: (cell) => tabulatorHandlersRef.current.formatarTempo((cell.getValue() as string) || ''),
            width: 378
        }
    ];

    useEffect(() => {
        // Se a referência da div não existe, não faz nada
        if (!tableRef.current) return;

        // Limpeza prévia para garantir recriação limpa ao mudar de empresa
        if (tabulatorRef.current) {
            tabulatorRef.current.destroy();
            tabulatorRef.current = null;
        }

        const initializeTable = async () => {
            console.log("🔧 Inicializando Tabela de Histórico...");

            // 1. Identificar Corporação
            const corporacaoName = selectedCorporationName || "";
            let colunasConfiguradas: any[] = [];

            // 2. Buscar Labels na API (HISTORICO_ASSOCIACAO)
            if (corporacaoName && backendUrl && auth?.Token) {
                try {
                    const encodedCorp = encodeURIComponent(corporacaoName);
                    const endpoint = `/api/labels/HISTORICO_ASSOCIACAO/${encodedCorp}`;
                    
                    const response = await fetch(`${backendUrl}${endpoint}`, {
                        method: 'GET',
                        headers: {
                            'Accept': 'application/json',
                            'Authorization': `Bearer ${auth.Token}`
                        }
                    });

                    if (response.ok) {
                        const json = await response.json();
                        if (json.IsRequestSuccessful && json.data) {
                            colunasConfiguradas = json.data;
                            console.log("✅ Labels HISTORICO_ASSOCIACAO carregados:", colunasConfiguradas);
                        } else if (json.payload) {
                             // Fallback para estrutura de resposta padrão do seu hook useHttpClient
                            colunasConfiguradas = json.payload;
                        }
                    }
                } catch (error) {
                    console.error("❌ Erro ao carregar labels do histórico:", error);
                }
            }

            // 3. Obter colunas base
            const baseColumns = getHistoricoBaseColumns();

            // 4. Merge: Aplicar títulos dinâmicos
            const finalColumns: ColumnDefinition[] = baseColumns.map(col => {
                const config = colunasConfiguradas.find(
                    c => c.Field && col.field && c.Field.toLowerCase() === col.field.toLowerCase()
                );

                if (config && config.Title) {
                    return { ...col, title: config.Title };
                }
                return col;
            });

            // 5. Inicializar Tabulator
            tabulatorRef.current = new Tabulator(tableRef.current!, {
                data: historico, // Passa os dados atuais para não nascer vazia
                layout: 'fitColumns',
                height: 520,
                placeholder: 'Nenhum histórico encontrado.',
                columnHeaderVertAlign: 'bottom',
                resizableColumnFit: true,
                movableColumns: true,
                columns: finalColumns
            });

            // 6. Eventos
            tabulatorRef.current.on('rowClick', (e: any, row: any) => {
                const data = row.getData() as HistoricoAtivo;
                if (tabulatorHandlersRef.current.onRowClick) {
                    tabulatorHandlersRef.current.onRowClick(data);
                }
            });
        };

        initializeTable();

        return () => {
            if (tabulatorRef.current) {
                tabulatorRef.current.destroy();
                tabulatorRef.current = null;
            }
        };
    }, [dateRangeHeaderFilterEditor, dateRangeHeaderFilterFunc, selectedCorporationName, historico]);

    useEffect(() => {
        if (tabulatorRef.current && historico.length > 0) {
            console.log('📊 [HISTÓRICO] Atualizando tabela com', historico.length, 'registros');
            tabulatorRef.current.replaceData(historico);
        }
    }, [historico]);

    // Função para exportar histórico para Excel
    const exportHistoricoToExcel = useCallback(async () => {
        if (!tabulatorRef.current || !backendUrl) {
            alert.createMessage({
                icon: 'warning',
                title: 'Aviso',
                message: 'Não há dados para exportar.',
            });
            return;
        }

        try {
            console.log('📥 [HISTÓRICO] Fazendo download do Excel');

            // Pegar dados filtrados da tabela (dados visíveis/filtrados)
            const filteredData = tabulatorRef.current.getData("active");
            const visibleColumns = tabulatorRef.current.getColumns().filter((col: ColumnComponent) => col.isVisible());

            console.log('📊 [HISTÓRICO] Dados filtrados:', filteredData.length, 'registros');
            console.log('📊 [HISTÓRICO] Colunas visíveis:', visibleColumns.length, 'colunas');

            if (filteredData.length === 0) {
                alert.createMessage({
                    icon: 'warning',
                    title: 'Aviso',
                    message: 'Não há dados para exportar.',
                });
                return;
            }

            // Mapeamento de campos para títulos
            const fieldToTitle: { [key: string]: string } = {
                ativo: "Ativo",
                veiculo: "Veículo",
                associadoEm: "Associado em",
                desassociadoEm: "Desassociado em",
                tempo: "Tempo",
                duracaoMinutos: "Duração (minutos)"
            };

            // Mapear os dados usando os títulos das colunas
            const orderedData = filteredData.map((row: Record<string, any>) => {
                const orderedRow: Record<string, any> = {};
                visibleColumns.forEach((col: ColumnComponent) => {
                    const field = col.getField();
                    const title = fieldToTitle[field] || col.getDefinition().title || field;
                    if (row.hasOwnProperty(field)) {
                        orderedRow[title] = row[field] ?? "";
                    }
                });
                return orderedRow;
            });

            const endpoint = `${backendUrl}/Report/Excel`;
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth?.Token}`
                },
                body: JSON.stringify(orderedData)
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5).replace('T', '_');
                a.download = `Historico_Associacoes_${timestamp}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                console.log('✅ [HISTÓRICO] Arquivo Excel baixado com sucesso!');
                alert.createMessage({
                    icon: 'success',
                    title: 'Sucesso',
                    message: 'Arquivo Excel baixado com sucesso!',
                });
            } else {
                console.error('❌ [HISTÓRICO] Erro ao baixar o arquivo Excel:', response.statusText);
                alert.createMessage({
                    icon: 'error',
                    title: 'Erro',
                    message: 'Erro ao gerar o arquivo Excel.',
                });
            }
        } catch (error) {
            console.error('❌ [HISTÓRICO] Erro ao fazer download do Excel:', error);
            alert.createMessage({
                icon: 'error',
                title: 'Erro',
                message: 'Erro ao tentar exportar para Excel.',
            });
        }
    }, [tabulatorRef, backendUrl, auth?.Token, alert]);

    return (
        <Box sx={{ mt: 3, mb: 3, position: 'relative', zIndex: 'inherit' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
                    {onOpenFiltros && (
                        <Button
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            onClick={onOpenFiltros}
                            sx={{ minWidth: 150 }}
                        >
                            Filtros
                            {filtrosCount > 0 && (
                                <Chip
                                    label={filtrosCount}
                                    size="small"
                                    color="primary"
                                    sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
                                />
                            )}
                        </Button>
                    )}
                    <TextField
                        label="Dias de Histórico"
                        type="number"
                        value={diasHistorico}
                        onChange={handleDiasHistoricoChange}
                        size="small"
                        inputProps={{ min: 1 }}
                        sx={{ width: 150 }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<GetAppIcon />}
                        onClick={exportHistoricoToExcel}
                        disabled={!canDisplay || historico.length === 0 || loadingHistorico}
                        sx={{ minWidth: 150 }}
                    >
                        Download Excel
                    </Button>
                </Box>
            </Box>
            <Box
                sx={{
                    position: 'relative',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    minHeight: 360,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    zIndex: 'inherit',
                    '@media (max-width: 768px)': {
                        overflow: 'auto'
                    }
                }}
            >
                <Box sx={{ opacity: canDisplay ? 1 : 0.4, pointerEvents: canDisplay ? 'auto' : 'none', bgcolor: 'background.paper', position: 'relative', zIndex: 'inherit' }}>
                    <div ref={tableRef} style={{ minHeight: 340, position: 'relative' }} />
                </Box>
                {!canDisplay && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.paper', textAlign: 'center', px: 4, zIndex: 'inherit' }}>
                        <Typography variant="body2" color="text.secondary">
                            Selecione uma corporação e organização para visualizar o histórico.
                        </Typography>
                    </Box>
                )}
                {loadingHistorico && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.9)', zIndex: 'inherit' }}>
                        <CircularProgress />
                    </Box>
                )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Linhas por página</InputLabel>
                        <Select
                            value={linhas}
                            label="Linhas por página"
                            onChange={handleLinhasChange}
                        >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            onClick={handlePaginaAnterior}
                            disabled={offset === 0 || loadingHistorico}
                            size="small"
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'center' }}>
                            Página {getPaginaAtual()} de {getTotalPaginas() || 1}
                        </Typography>
                        <IconButton
                            onClick={handleProximaPagina}
                            disabled={
                                loadingHistorico ||
                                (totalHistorico > 0 && offset + linhas >= totalHistorico) ||
                                (totalHistorico === 0 && historico.length < linhas)
                            }
                            size="small"
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            {/* Estilos Mobile para Histórico */}
            <style>{`
                /* Estilos mobile para Tabulator - Histórico */
                @media (max-width: 768px) {
                    /* Container da tabela com rolagem horizontal e cursor grab */
                    .tabulator-tableholder {
                        cursor: grab !important;
                        user-select: none !important;
                        -webkit-overflow-scrolling: touch !important;
                    }

                    .tabulator-tableholder:active {
                        cursor: grabbing !important;
                    }

                    /* Permitir que a tabela use sua largura natural */
                    .tabulator-table {
                        width: auto !important;
                    }

                    /* Melhorar legibilidade do texto em mobile */
                    .tabulator-cell {
                        font-size: 13px !important;
                        padding: 10px 8px !important;
                        line-height: 1.4 !important;
                        white-space: nowrap !important;
                    }

                    .tabulator-col-title {
                        font-size: 13px !important;
                        font-weight: 600 !important;
                        padding: 10px 8px !important;
                        white-space: nowrap !important;
                    }

                    /* Ajustar filtros do cabeçalho */
                    .tabulator-header-filter input {
                        font-size: 12px !important;
                        padding: 6px 8px !important;
                        min-height: 32px !important;
                    }

                    /* Indicador visual de rolagem */
                    .tabulator:after {
                        content: "← Arraste para ver mais →" !important;
                        position: absolute !important;
                        bottom: 10px !important;
                        left: 50% !important;
                        transform: translateX(-50%) !important;
                        background: rgba(0, 0, 0, 0.7) !important;
                        color: white !important;
                        padding: 6px 12px !important;
                        border-radius: 4px !important;
                        font-size: 11px !important;
                        pointer-events: none !important;
                        z-index: 100 !important;
                        white-space: nowrap !important;
                        animation: fadeInOut 3s ease-in-out !important;
                    }

                    @keyframes fadeInOut {
                        0%, 100% { opacity: 0; }
                        10%, 90% { opacity: 1; }
                    }

                    /* Melhorar controles de paginação em mobile */
                    .MuiBox-root > .MuiBox-root {
                        flex-wrap: wrap !important;
                        gap: 12px !important;
                    }

                    .MuiFormControl-root[size="small"] {
                        min-width: 100px !important;
                    }

                    /* Ajustar botões */
                    .MuiButton-contained,
                    .MuiButton-outlined {
                        font-size: 13px !important;
                        padding: 8px 16px !important;
                    }

                    /* Título mais compacto */
                    .MuiTypography-h6 {
                        font-size: 1.1rem !important;
                    }
                }
            `}</style>
        </Box>
    );
};

export default HistoricTab;

